import { initializeApp } from "firebase/app";
// NOTE: Do not statically import firebase/messaging. It breaks on native WebView.
import { firebaseConfig } from "./const/Settings";

const app = initializeApp(firebaseConfig);


// 네이티브(Capacitor 앱) 판별
export function isNativeApp(): boolean {
  try {
    const cap = (window as any).Capacitor;
    if (cap && typeof cap.getPlatform === 'function') {
      return cap.getPlatform() !== 'web';
    }
  } catch { }
  return false;
}

// Service Worker 지원 환경(PWA 웹) 판별
export function isServiceWorkerSupported(): boolean {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    if (isNativeApp()) return false;
    const sw: any = (navigator as any).serviceWorker;
    // 일부 WebView에서는 'serviceWorker' in navigator 가 true여도 값이 undefined일 수 있음
    if (!sw) return false;
    if (typeof sw.addEventListener !== 'function') return false;
    // 보안 컨텍스트가 아니면 SW 동작 안 함
    if ((window as any).isSecureContext === false) return false;
    return true;
  } catch {
    return false;
  }
}

export async function requestPushPermission() {
  // SW 미지원 또는 네이티브 앱에서는 실행하지 않음
  if (!isServiceWorkerSupported()) {
    console.info('[PWA] Service Worker 미지원/네이티브 환경: FCM 웹 토큰 요청 생략');
    return null;
  }
  if (typeof Notification === 'undefined') {
    console.warn('[PWA] Notification API 미지원');
    return null;
  }
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch (e) {
    // Apple Mail 링크 프리뷰 샌드박스 / 알림이 앱 레벨에서 차단된 WebKit 환경에서는
    // "Notifications are not allowed for this application" 을 throw. 정상적인 거부로 처리.
    console.info('[PWA] Notification.requestPermission 불가(차단 환경):', e);
    return null;
  }
  if (permission === 'granted') {
    return await getFCMToken();
  }
  return null;
}

export async function getFCMToken(): Promise<string | null> {
  if (!isServiceWorkerSupported()) return null;
  try {
    const mod = await import('firebase/messaging');
    // iOS Safari 등은 messaging 지원이 false일 수 있음
    if (typeof mod.isSupported === 'function') {
      const supported = await mod.isSupported();
      if (!supported) {
        console.info('[PWA] Firebase messaging 미지원 브라우저입니다.');
        return null;
      }
    }
    const messaging = mod.getMessaging(app);
    const token = await mod.getToken(messaging, { vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY });
    // console.log("[PWA] FCM token obtained:", token);
    return token;
  } catch (e: any) {
    // 사용자가 알림 권한을 거부한 경우 — 정상적인 사용자 선택
    const msg = e?.message || e?.code || '';
    if (msg.includes('not allowed') || msg.includes('NotAllowedError') || msg.includes('permission')) {
      console.info('[PWA] 알림 권한이 허용되지 않아 FCM 토큰을 가져올 수 없습니다.');
    } else {
      console.error('[PWA] getToken 실패', e);
    }
    return null;
  }
}

// 현재 앱이 PWA(Standalone) 환경인지 체크
export function isPWA(): boolean {
  // iOS, Android, 데스크탑 모두 대응
  return (
    window.matchMedia('(display-mode: standalone)').matches
  );
}

// 안전한 SW message 리스너 연결 헬퍼
export function attachSWMessageListener(handler: (e: MessageEvent) => void) {
  try {
    if (!isServiceWorkerSupported()) {
      console.info('[PWA] SW 미지원 환경: message 리스너 생략');
      return;
    }
    const sw: any = (navigator as any).serviceWorker;
    if (sw && typeof sw.addEventListener === 'function') {
      sw.addEventListener('message', handler);
    } else {
      console.info('[PWA] navigator.serviceWorker가 예상과 달라 리스너를 등록하지 않습니다.');
    }
  } catch (e) {
    console.warn('SW message listener 등록 실패', e);
  }
}

// Foreground Message Listener (Main Thread)
export async function initForegroundMessageListener(handler: (payload: any) => void) {
  if (!isServiceWorkerSupported()) return;

  try {
    const mod = await import('firebase/messaging');
    if (typeof mod.isSupported === 'function' && !(await mod.isSupported())) {
      return;
    }
    const messaging = mod.getMessaging(app);
    mod.onMessage(messaging, (payload) => {
      console.log('[PWA] onMessage received:', payload);
      handler(payload);

      // Show a simple browser notification if permission granted (and not handled by SW)
      // Note: onMessage usually triggers when app is in foreground (focused).
      // SW handles background.
      if (Notification.permission === 'granted') {
        const title = payload.notification?.title || 'New Message';
        const options = {
          body: payload.notification?.body,
          icon: '/assets/icon/favicon.png',
          data: payload.data
        };
        // new Notification(title, options); // Optional: System notification in foreground
      }
    });
    console.log('[PWA] Foreground message listener initialized');
  } catch (e) {
    console.error('[PWA] Failed to init foreground listener', e);
  }
}
